import { useNavigate } from 'react-router-dom'

interface CreatedCustomer {
  id: string
  name: string
  action: 'created' | 'linked'
}

interface JobCreationResultStepProps {
  parentJobId: string
  parentJobRef: string
  childJobIds?: string[]
  childJobRefs?: string[]
  customersCreated?: CreatedCustomer[]
  onCreateAnother: () => void
}

export default function JobCreationResultStep({
  parentJobId,
  parentJobRef,
  childJobIds = [],
  childJobRefs = [],
  customersCreated = [],
  onCreateAnother,
}: JobCreationResultStepProps) {
  const navigate = useNavigate()

  const totalJobsCreated = 1 + (childJobIds?.length || 0)

  return (
    <div className="max-w-2xl mx-auto py-8 space-y-8">
      {/* Success Banner */}
      <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-6 rounded-lg">
        <div className="flex items-start gap-4">
          <div className="text-4xl">✓</div>
          <div>
            <h2 className="text-2xl font-bold text-green-900 dark:text-green-100 mb-2">
              Successfully Created {totalJobsCreated} Job{totalJobsCreated > 1 ? 's' : ''}
            </h2>
            <p className="text-green-700 dark:text-green-300">
              All jobs have been saved to the system
            </p>
          </div>
        </div>
      </div>

      {/* Job References */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Job References</h3>

        {/* Parent Job or Single Job */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
            {childJobRefs.length === 0 ? 'Job' : 'Parent Job'}
          </p>
          <button
            onClick={() => navigate(`/jobs/${parentJobId}`)}
            className="text-xl font-bold text-blue-700 dark:text-blue-300 hover:underline cursor-pointer"
          >
            {parentJobRef} →
          </button>
        </div>

        {/* Child Jobs */}
        {childJobRefs && childJobRefs.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
              {childJobRefs.length} Child Job{childJobRefs.length > 1 ? 's' : ''}
            </p>
            <div className="grid gap-2 md:grid-cols-2">
              {childJobRefs.map((ref, idx) => (
                <button
                  key={ref}
                  onClick={() => childJobIds[idx] && navigate(`/jobs/${childJobIds[idx]}`)}
                  className="bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-lg p-3 text-left transition"
                >
                  <p className="font-semibold text-gray-900 dark:text-white">{ref}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Click to view →</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Customers Created */}
      {customersCreated && customersCreated.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {customersCreated.filter(c => c.action === 'created').length} New Customer
            {customersCreated.filter(c => c.action === 'created').length > 1 ? 's' : ''} Created
          </h3>

          <div className="space-y-2">
            {customersCreated.map(customer => (
              <div
                key={customer.id}
                className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3 flex items-center gap-2"
              >
                <span className="text-lg">
                  {customer.action === 'created' ? '🆕' : '🔗'}
                </span>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">{customer.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {customer.action === 'created' ? 'Created in system' : 'Linked to existing'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onCreateAnother}
          className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
        >
          Create Another
        </button>
        <button
          onClick={() => navigate('/jobs')}
          className="px-4 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition"
        >
          Go to Jobs List
        </button>
      </div>
    </div>
  )
}
